import { useState, useCallback, useEffect } from "react";
import {
  useGetAllInvoicesQuery,
  useDeleteInvoiceMutation,
} from "../api/invoiceApi";
import { InvoiceResponseType } from "../types/invoiceResponse";

// Just alias InvoiceResponseType as FeeItem for backward compatibility
export type FeeItem = InvoiceResponseType;

export const useInvoiceManagement = () => {
  const [selectedCenter, setSelectedCenter] = useState("all");
  const [filters, setFilters] = useState({
    size: 10,
    page: 1,
    search: "",
    center_id: undefined as number | undefined,
    course_id: undefined as number | undefined,
    status: undefined as string | undefined,
    fee_type: undefined as string | undefined,
  });

  const {
    data: invoicesData,
    isLoading: loading,
    error: apiError,
    refetch,
  } = useGetAllInvoicesQuery(filters);

  const error = apiError ? "Failed to fetch invoices" : null;

  // Get fees from API response - updated to use items instead of data
  const fees = invoicesData?.data?.items || [];

  // Transform student data
  const transformedFees = fees.map(
    (fee: any): FeeItem => ({
      ...fee,
      student: fee.student
        ? {
            id: fee.student.id,
            name: `${fee.student.first_name} ${fee.student.last_name}`,
          }
        : undefined,
    })
  );

  // Fetch fees with current filters
  const fetchFees = useCallback(() => {
    refetch();
  }, [refetch]);

  // Update filters - MODIFIED to not reset page when changing pages
  const updateFilters = useCallback((newFilters: Partial<typeof filters>) => {
    setFilters((prev) => {
      // Don't reset page to 1 if we're explicitly changing the page
      if (newFilters.page !== undefined) {
        return { ...prev, ...newFilters };
      }
      // Reset page to 1 for other filter changes
      return { ...prev, ...newFilters, page: 1 };
    });
  }, []);

  // Get filtered fees based on center
  const getFilteredFees = useCallback(() => {
    if (selectedCenter === "all") return transformedFees;
    return transformedFees.filter(
      (fee: FeeItem) => fee.center.id.toString() === selectedCenter
    );
  }, [transformedFees, selectedCenter]);

  // Get center options for dropdown
  const getCenterOptions = useCallback(() => {
    const centerOptions = [
      { value: "all", label: "All Centers" },
      ...Array.from(
        new Set(transformedFees.map((fee: FeeItem) => fee.center.id))
      ).map((centerId) => {
        const center = transformedFees.find(
          (fee: FeeItem) => fee.center.id === centerId
        )?.center;
        return {
          value: centerId.toString(),
          label: center?.name || `Center ${centerId}`,
        };
      }),
    ];
    return centerOptions;
  }, [transformedFees]);

  // Get course options for dropdown
  const getCourseOptions = useCallback(() => {
    return Array.from(
      new Set(
        transformedFees
          .filter((f: FeeItem) => f.course)
          .map((fee: FeeItem) => fee.course?.id)
      )
    ).map((courseId) => {
      const course = transformedFees.find(
        (fee: FeeItem) => fee.course?.id === courseId
      )?.course;
      return {
        value: courseId?.toString() || "",
        label: course?.name || `Course ${courseId}`,
      };
    });
  }, [transformedFees]);

  // Pagination helpers
  const paginationData = {
    totalPages: invoicesData?.data?.totalPages || 0,
    pageSize: invoicesData?.data?.pageSize || 10,
    totalItems: invoicesData?.data?.totalItems || 0,
    currentPage: filters.page || 1, // Use the current filter page as source of truth
  };

  const [deleteInvoice, { isLoading: isDeleting }] = useDeleteInvoiceMutation();

  const handleDeleteInvoice = useCallback(
    async (id: number | string) => {
      try {
        const response = await deleteInvoice({ id }).unwrap();
        if (response.success) {
          refetch();
          return true;
        }
        return false;
      } catch (error) {
        console.error("Error deleting invoice:", error);
        throw error;
      }
    },
    [deleteInvoice, refetch]
  );

  return {
    fees: transformedFees,
    loading,
    error,
    selectedCenter,
    setSelectedCenter,
    fetchFees,
    // collectFee,
    getFilteredFees,
    getCenterOptions,
    getCourseOptions,
    updateFilters,
    paginationData,
    filters,
    handleDeleteInvoice,
    isDeleting,
  };
};
